import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { Button, IconButton, Typography } from '@material-ui/core'
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocationOnOutlined as LocationOnOutlinedIcon
} from '@material-ui/icons'
import automovilismo from 'assets/img/automovilismo.jpg'
import biking from 'assets/img/biking.jpg'
import fbi from 'assets/img/fbi.jpg'
import kayak from 'assets/img/kayak.jpg'
import motocross from 'assets/img/motocross.jpg'
import mountainBike from 'assets/img/mountain-bike.jpg'
import natacion from 'assets/img/natacion.jpg'
import ocr from 'assets/img/ocr.jpg'
import running from 'assets/img/running.jpg'
import speedway from 'assets/img/speedway.jpg'
import trekking from 'assets/img/trekking.jpg'
import clsx from 'clsx'
import moment from 'moment'
import { ROUTES } from 'routes'
import { ShareUrlMenuDialog } from 'shared'
import { EventCardPreview } from 'shared/EventCardPreview'
import { verifyUserNotLogged } from 'state/modules/app'
import { reactEvent } from 'state/modules/events'
import { EVENT_STATES } from 'utils/constants'

import { useStyles } from './EventCard.style'

const getDateEventFormat = (fromDate, toDate) => {
  const fromDateFormat = fromDate.format('YYYY-MM-DD')
  const toDateFormat = toDate.format('YYYY-MM-DD')
  const dateFormats = ['DD MMM YY', 'DD MMM', 'DD']

  const toDateResult = toDate.format(dateFormats[0]).replace('.', '')

  if (moment(fromDateFormat).isSame(toDateFormat))
    return fromDate.format(dateFormats[0]).replace('.', '')

  if (moment(fromDateFormat).year() !== moment(toDateFormat).year())
    return `${fromDate.format(dateFormats[0])} - ${toDateResult}`.replace('.', '')

  if (moment(fromDateFormat).month() !== moment(toDateFormat).month())
    return `${fromDate.format(dateFormats[1])}/${toDateResult}`.replace('.', '')
  else return `${fromDate.format(dateFormats[2])}/${toDateResult}`.replace('.', '')
}

const DISCIPLINE_IMAGES = {
  automovilismo,
  biking,
  fbi,
  kayak,
  motocross,
  mountainBike,
  natacion,
  running,
  speedway,
  trekking,
  ocr
}

const EventCard = ({ event, disabledAction, isFinished, hiddenInscriptionButton }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const { pendingReact } = useSelector((state) => state.events)

  const {
    id,
    from,
    to,
    disciplines,
    hasLiveResults,
    name,
    loggedUserMetadata,
    areaLevel1Name,
    countryName,
    isSuspended,
    likesCount,
    hasResults,
    createdAt,
    state,
    updatedAt,
    organizations,
    slugUrl,
    externalInscriptionUrl,
    address,
    longitude,
    latitude,
    circuit,
    schedule,
    profileImages,
    rulesUrl,
    activeInscriptionFormOpenFrom,
    activeInscriptionFormOpenTo,
    activeInscriptionFormIsDraft,
    activeInscriptionFormId,
    hasRulesDocumentFile,
    championship,
    hasQuota = true
  } = event

  const toLocal = moment(to)
  const fromLocal = moment(from)
  const afterEvent = toLocal < moment()

  const isOrganizer = loggedUserMetadata?.canEdit

  const isActiveInscription =
    !hiddenInscriptionButton &&
    state === EVENT_STATES.ACTIVE &&
    ((!!activeInscriptionFormId &&
      !activeInscriptionFormIsDraft &&
      moment(activeInscriptionFormOpenFrom).isSameOrBefore(moment()) &&
      moment(activeInscriptionFormOpenTo).isSameOrAfter(moment())) ||
      externalInscriptionUrl)

  const hasActiveSchedule = schedule.some(
    (x) =>
      fromLocal.isSameOrBefore(x.scheduleDate) &&
      toLocal.isSameOrAfter(x.scheduleDate) &&
      !!x.description
  )

  const hasMoreInformation =
    isActiveInscription ||
    !!externalInscriptionUrl ||
    isOrganizer ||
    hasResults ||
    hasActiveSchedule ||
    hasRulesDocumentFile ||
    !!circuit ||
    !!rulesUrl

  const countDisciplines = disciplines.length - 1
  const disciplinesToShow =
    countDisciplines <= 0
      ? disciplines
      : [
          ...disciplines.slice(0, 1),
          {
            id: 'add',
            name: `Y ${countDisciplines} más`,
            title: disciplines
              .map((e) => e.name)
              .slice(1, disciplines.length)
              .join(', ')
          }
        ]

  const eventDateFormat = getDateEventFormat(fromLocal, toLocal)
  const hasCoords = latitude !== 0 && longitude !== 0
  const locationEvent = [countryName, areaLevel1Name, address]
    .filter(Boolean)
    .join(', ')
    .concat(`. ${!hasCoords ? ' La ubicación exacta se informará próximamente' : ''}`)

  const hasProfileImage = Array.isArray(profileImages) && profileImages.length > 0

  const activeOrganizations = organizations
    .filter((e) => !!e.acceptedAt)
    .sort((x, y) => x.acceptedAt - y.acceptedAt)

  const organizationLinks = activeOrganizations.map(
    ({ id, organizationHandle, organizationName, organizationId }, index) => (
      <span key={id}>
        <Link
          to={`${ROUTES.ORGANIZATIONS.PROFILE}/${organizationHandle || organizationId}`}
          className={classes.organization}
          onClick={(e) => e.stopPropagation()}>
          {organizationHandle || organizationName}
        </Link>

        {index < activeOrganizations.length - 1 && ' & '}
      </span>
    )
  )

  const organizationsToShow = activeOrganizations
    .map(({ organizationName, organizationHandle }) => organizationHandle || organizationName)
    .join(' & ')

  const handleActionEvent = (e, action) => {
    e?.preventDefault()
    e?.stopPropagation()

    if (!user) {
      dispatch(verifyUserNotLogged({ state: true }))
    } else {
      dispatch(action(event, false))
    }
  }

  const handleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (afterEvent || hasMoreInformation) {
      history.push(`${ROUTES.EVENTS.PROFILE}/${slugUrl}`)
    } else {
      setIsModalOpen(true)
    }
  }

  const options = disciplines.map((x) => ({
    ...x,
    imgUrl: DISCIPLINE_IMAGES[x.imageName],
    className: clsx(x.imageName && classes[x.imageName])
  }))

  return (
    <div className={classes.card} id='event-card' onClick={handleClick}>
      <div
        className={clsx(
          classes.headerContainer,
          hasResults && classes.headerContainerResult,
          isFinished && classes.headerContainerResult
        )}>
        <Typography className={classes.date}>{eventDateFormat}</Typography>
        <div className={classes.sportContainer}>
          {hasLiveResults ? (
            <div className={classes.liveContainer}>
              <div className={clsx(classes.sport, classes.liveSport)}>EN VIVO</div>
            </div>
          ) : (
            disciplinesToShow.map((d) => (
              <div className={classes.sport} key={d.id} title={d.title}>
                {d.name}
              </div>
            ))
          )}
        </div>
      </div>
      <div className={classes.featureImageContainer} id='feature-image-container'>
        {!hasQuota && !hasResults && !afterEvent && !!activeInscriptionFormId && (
          <div className={clsx(classes.banner, classes.noQuotaBanner)}>
            <Typography className={classes.altBannerTitle}>AGOTADO</Typography>
          </div>
        )}

        {state === EVENT_STATES.SUSPENDED && (
          <div className={clsx(classes.banner, classes.suspendedBanner)}>
            <Typography className={classes.bannerTitle}>SUSPENDIDO</Typography>
          </div>
        )}

        {state === EVENT_STATES.CANCELLED && (
          <div className={clsx(classes.banner, classes.cancelledBanner)}>
            <Typography className={classes.bannerTitle}>CANCELADO</Typography>
          </div>
        )}

        {hasProfileImage ? (
          <div
            className={classes.featureImage}
            id='feature-image'
            style={{ backgroundImage: `url(${profileImages[0]})` }}></div>
        ) : (
          options
            .filter((x) => !!x.imageName)
            .map((x) => (
              <div
                key={x.id}
                className={clsx(
                  classes.featureImage,
                  x.className,
                  `size-${options.filter((x) => !!x.imageName).length}`
                )}
                style={{ backgroundImage: `url(${x.imgUrl})` }}
              />
            ))
        )}
      </div>

      <div className={clsx(classes.actionButtonContainer, isSuspended && 'suspended')}>
        {!disabledAction && (
          <div className={classes.actionSocialBarButton}>
            <IconButton
              className={classes.actionButton}
              disabled={pendingReact}
              onClick={(e) => handleActionEvent(e, reactEvent)}>
              {event?.loggedUserMetadata && event.loggedUserMetadata.like ? (
                <FavoriteIcon />
              ) : (
                <FavoriteBorderIcon />
              )}
              {!!likesCount && <em>{likesCount}</em>}
            </IconButton>
            <ShareUrlMenuDialog
              id={id}
              updatedAt={updatedAt ?? createdAt}
              type='event'
              isHiddenLabel
              isOutlineIcon
              buttonClassName={classes.shareButton}
              copyMessage='El link del evento fue copiado al portapapeles'
              suspended={isSuspended}
              slugUrl={slugUrl}
              title={name}
            />
          </div>
        )}
        {championship && (
          <Button
            className={clsx(classes.actionButton, classes.championshipButton)}
            component={Link}
            onClick={(e) => e.stopPropagation()}
            to={`${ROUTES.CHAMPIONSHIPS.PROFILE}/${championship.slugUrl}`}
            color='primary'>
            Ver Campeonato
          </Button>
        )}
      </div>

      <div className={classes.nameEventContainer}>
        <Typography
          color='primary'
          variant='h5'
          className={clsx(classes.nameEvent, isSuspended && 'suspended')}
          title={name}>
          {name}
        </Typography>
      </div>

      <div className={classes.textContainer}>
        <div className={classes.organizationContainer}>
          <Typography className={classes.organization} title={`Organiza ${organizationsToShow}`}>
            Organiza {organizationLinks}
          </Typography>
        </div>

        <div className={classes.locationContainer}>
          {hasCoords && (
            <LocationOnOutlinedIcon color='primary' style={{ width: 25, height: 25 }} />
          )}
          <Typography component='p' title={locationEvent}>
            {locationEvent}
          </Typography>
        </div>
      </div>
      <div className={classes.endContainer}>
        {hasResults ? (
          <Link to={`${ROUTES.EVENTS.PROFILE}/${slugUrl}`} className={classes.link}>
            Ver resultados
          </Link>
        ) : (
          <>
            <Link onClick={handleClick} className={classes.link} to='#'>
              Ver más
            </Link>
            {isActiveInscription &&
              (!externalInscriptionUrl ? (
                <Button
                  to={`/events/${slugUrl}/inscription`}
                  className={clsx(classes.linkInscription, !hasQuota && 'disabled')}
                  color='secondary'
                  component={Link}
                  onClick={(e) => e?.stopPropagation()}
                  disabled={!hasQuota}>
                  {!loggedUserMetadata?.hasActiveInscription ? 'Inscribirme' : 'Inscripción'}
                </Button>
              ) : (
                <Button
                  href={externalInscriptionUrl}
                  color='secondary'
                  className={classes.linkInscription}
                  target='_blank'
                  onClick={(e) => e?.stopPropagation()}
                  rel='noopener noreferrer'>
                  Inscribirme
                </Button>
              ))}
            <EventCardPreview
              open={isModalOpen}
              like={!!event.loggedUserMetadata && event.loggedUserMetadata.like}
              disciplinesToShow={disciplinesToShow}
              imageUrl={hasProfileImage ? profileImages[0] : null}
              eventDisciplines={options}
              eventDateFormat={eventDateFormat}
              organizationsToShow={organizationsToShow}
              hasCoords={hasCoords}
              locationEvent={locationEvent}
              afterEvent={afterEvent}
              setIsModalOpen={setIsModalOpen}
              event={event}
            />
          </>
        )}

        {disabledAction && (
          <ShareUrlMenuDialog
            id={id}
            updatedAt={updatedAt ?? createdAt}
            type='event'
            isHiddenLabel
            isOutlineIcon
            buttonClassName={clsx(classes.shareButton, 'alt')}
            copyMessage='El link del evento fue copiado al portapapeles'
            suspended={isSuspended}
            slugUrl={slugUrl}
            title={name}
          />
        )}
      </div>
    </div>
  )
}

export default EventCard
